import { useCallback, useEffect, useState } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';
import { useCurrentBreakpointValue } from '@change-corgi/design-system-legacy/responsive';

import { getPetitionsInMovement } from 'src/app/pages/movement/shared/api';
import type { PetitionsInMovement } from 'src/app/pages/movement/shared/types';
import { useConfirm } from 'src/app/shared/hooks/modals';

import { useRemovePetitionFromMovement } from './useRemovePetitionFromMovement';

const PETITIONS_LIMIT_ON_DESKTOP = 6;
const PETITIONS_LIMIT_ON_MOBILE = 3;

// eslint-disable-next-line max-lines-per-function
export function useGetPetitionsInMovement(movementId: string): {
	petitionData: PetitionsInMovement | undefined;
	fetchMore: () => void;
	removePetition: (petitionId: string) => Promise<boolean>;
} {
	const desktop = useCurrentBreakpointValue([false, true]);
	const { translate } = useI18n();
	const confirmRemove = useConfirm({
		title: translate('fe.components.remove_movement_petition.title'),
		text: translate('fe.components.remove_movement_petition.desc'),
		confirmLabel: translate('fe.components.confirm_modal.buttons.delete'),
		confirmDataQa: 'modal-movement-remove-petition-button-ok',
		cancelLabel: translate('fe.components.confirm_modal.buttons.cancel'),
		cancelDataQa: 'modal-movement-remove-petition-button-cancel',
	});
	const { remove } = useRemovePetitionFromMovement();

	const utility = useUtilityContext();
	const [petitionData, setPetitionData] = useState<PetitionsInMovement>();
	const [offset, setOffset] = useState<number>(0);
	const [limit] = useState(desktop ? PETITIONS_LIMIT_ON_DESKTOP : PETITIONS_LIMIT_ON_MOBILE);

	const removePetition = useCallback(
		async (petitionId: string) => {
			if (!petitionData) return false;

			const result = await confirmRemove();
			if (!result) return false;

			// FIXME display error to user
			if (!(await remove(petitionId, movementId))) return false;

			const updatedPetitions = petitionData.items.filter((petition) => petition.id !== petitionId);
			setPetitionData({
				...petitionData,
				items: updatedPetitions,
				totalCount: petitionData.totalCount - 1,
			});
			return true;
		},
		[confirmRemove, petitionData, remove, movementId],
	);

	const fetchMore = () => {
		setOffset(offset + limit);
	};

	useEffect(() => {
		async function fetchData() {
			const result = await getPetitionsInMovement(movementId, limit, offset, utility);
			setPetitionData((current) => ({
				...result,
				items: [...(current?.items || []), ...result.items],
				totalCount: current?.totalCount || result.totalCount,
			}));
		}
		void fetchData();
	}, [limit, movementId, offset, utility]);

	return { petitionData, fetchMore, removePetition };
}
